import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Dumbbell, Heart, Zap, Star, Users, Activity } from 'lucide-react';
import axios from 'axios';
import { useLang } from '@/context/LanguageContext';
import { ProgramArt } from '@/components/ProgramArt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  combat: Flame, strength: Dumbbell, mind_body: Heart, cardio: Zap, dance: Star, aqua: Heart, specialized: Users,
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const ProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [filter, setFilter] = useState('all');
  const { t, pick, isAr } = useLang();

  useEffect(() => {
    axios.get(`${API}/programs`).then(r => setPrograms(r.data)).catch(() => {});
  }, []);

  const categories = ['all', ...new Set(programs.map(p => p.category))];
  const filtered = filter === 'all' ? programs : programs.filter(p => p.category === filter);

  return (
    <div className="pt-20 min-h-screen" data-testid="programs-page">
      {/* Header */}
      <section className="py-20 lg:py-28 bg-[#121215] border-b border-[rgba(255,255,255,0.06)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="overline mb-4">{t('programs.overline')}</p>
          <h1 className="text-5xl lg:text-6xl font-light mb-6">
            {t('programs.h_1')} <span className="font-bold gold-gradient-text">{t('programs.h_2')}</span>
          </h1>
          <p className="text-[#A3A19C] text-lg max-w-xl">
            {t('programs.sub')}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-[rgba(255,255,255,0.06)] sticky top-20 z-40 bg-[#08080A]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-3" data-testid="program-filters">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`filter-${cat}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === cat
                    ? 'bg-[#D4AF37] text-[#08080A]'
                    : 'border border-[rgba(255,255,255,0.1)] text-[#A3A19C] hover:border-[rgba(212,175,55,0.3)] hover:text-[#F5F2EB]'
                }`}
              >
                {t(`cat.${cat}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((program, i) => {
              const Icon = iconMap[program.category] || Star;
              return (
                <motion.div
                  key={program.id}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                  className="group bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden card-hover"
                  data-testid={`program-item-${i}`}
                >
                  <div className="relative h-52 overflow-hidden">
                    <ProgramArt program={program} alt={pick(program, 'name')} />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#D4AF37]">{t(`cat.${program.category}`)}</span>
                    </div>
                    <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-1">{pick(program, 'name')}</h3>
                    <p className="text-sm text-[#8C6C73] mb-3" dir="auto">{isAr ? program.name : program.name_ar}</p>
                    <p className="text-sm text-[#A3A19C] leading-relaxed mb-4 line-clamp-3">{pick(program, 'description')}</p>
                    <div className="flex items-center gap-4 text-xs text-[#A3A19C]">
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
                        program.intensity === 'high' ? 'bg-red-500/10 text-red-400' :
                        program.intensity === 'medium' ? 'bg-yellow-500/10 text-yellow-400' :
                        'bg-green-500/10 text-green-400'
                      }`}>
                        <Activity className="w-3.5 h-3.5" strokeWidth={1.5} /> {t(`intensity.${program.intensity}`)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-[#A3A19C] py-20">{t('programs.empty')}</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
